import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import MobileAgricultureWasteExpanded from './mobileagriculture-waste-expanded';
import AgricultureWasteExpanded from './agriculture-waste-expanded';
import HeavyMetalWasteExpanded from './heavy-metal-waste-expanded';
import MobileHeavyMetalWasteExpanded from './mobile-heavy-metal-waste-expanded';
import RadioactiveWasteExpanded from './radioactive-waste-expanded';
import MobileRadioactiveWasteExpanded from './mobile-radioactive-waste-expanded';
import SewageWasteExpanded from './sewage-waste-expanded';
import MobileSewageWasteExpanded from './mobilesewage-waste-expanded';

const DownArrowIcon = ({ className, ...props }) => (
  <svg
    width="19"
    height="10"
    viewBox="0 0 19 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    {...props}
  >
    <path d="M1 1L9.5 9L18 1" stroke="white" strokeWidth="1.5" />
  </svg>
);

const Navbar = ({ isSticky = false, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [previousExpanded, setPreviousExpanded] = useState(null);
  const menuRef = useRef(null);
  const navLinksRef = useRef(null);
  const [menuScale, setMenuScale] = useState(1);
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // return windowWidth;

  // console.log("windowWidth",windowWidth>768?"true":"flase")
  // List of navigation items for easy maintenance with their corresponding components
  const navItems = [
    {
      id: "agriculture-waste", text: "Agriculture waste", component: windowWidth > 768 ?
        AgricultureWasteExpanded
        : MobileAgricultureWasteExpanded
    },
    { id: "heavy-metal-waste", text: "Heavy metal waste", component: windowWidth > 768 ? HeavyMetalWasteExpanded : MobileHeavyMetalWasteExpanded },
    { id: "radioactive-waste", text: "Radioactive waste", component: windowWidth > 768 ? RadioactiveWasteExpanded : MobileRadioactiveWasteExpanded },
    { id: "sewage-waste", text: "Sewage waste", component: windowWidth > 768 ? SewageWasteExpanded : MobileSewageWasteExpanded }
  ];

  // Add cursor interaction to the navbar
  useEffect(() => {
    // Set data attributes for cursor inversion on navbar elements
    const navElements = document.querySelectorAll('.nav-menu, .nav-item, .nav-arrow-button');
    navElements.forEach(el => {
      el.setAttribute('data-cursor-invert', 'true');
    });

    // Ensure menu is properly marked for cursor inversion
    if (menuRef.current) {
      menuRef.current.setAttribute('data-cursor-invert', 'true');
    }
  }, [isOpen, expandedItem]);

  // Calculate and apply scaling to fit menu in viewport including the 5% gap
  useEffect(() => {
    if (isOpen && !expandedItem && navLinksRef.current) {
      const updateMenuScale = () => {
        const viewportHeight = window.innerHeight;
        const menuHeight = navLinksRef.current.scrollHeight;
        const headerHeight = 60; // Approximate height of close button
        const topGap = viewportHeight * 0.05; // 5% of viewport height
        const footerBuffer = 20; // Space at bottom

        // Available height now accounts for the 5% top gap
        const availableHeight = viewportHeight - headerHeight - footerBuffer - topGap;

        if (menuHeight > availableHeight) {
          // Calculate scale needed to fit
          const newScale = availableHeight / menuHeight;
          // Don't scale below 0.65 to maintain readability
          setMenuScale(Math.max(0.65, newScale));
        } else {
          setMenuScale(1);
        }
      };

      updateMenuScale();
      window.addEventListener('resize', updateMenuScale);
      return () => window.removeEventListener('resize', updateMenuScale);
    } else if (expandedItem) {
      // Reset scale when an item is expanded to allow normal scrolling
      setMenuScale(1);
    }
  }, [isOpen, expandedItem]);

  // Track previous expanded state when it changes
  useEffect(() => {
    if (expandedItem) {
      setPreviousExpanded(expandedItem);
    }
  }, [expandedItem]);

  // Handle scrolling when expanded state changes
  useEffect(() => {
    if (!menuRef.current) return;

    // If we have a previously expanded item but now nothing is expanded (item was unexpanded)
    if (previousExpanded && !expandedItem) {
      // Scroll back to top with a slight delay to allow for transition
      setTimeout(() => {
        if (menuRef.current) {
          menuRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 50); // Delay to allow CSS transitions to start
    }
    // If something is expanded, handle scroll to the expanded item
    else if (expandedItem) {
      const scrollTimer = setTimeout(() => {
        const element = menuRef.current?.querySelector('.expanded');
        if (element) {
          // Calculate if this is the last item
          const isLastItem = element === element.parentNode.lastElementChild;

          // Adjust scroll behavior based on position
          element.scrollIntoView({
            behavior: 'smooth',
            // Use 'start' for most items, but 'center' for last item
            block: isLastItem ? 'center' : 'start'
          });

          // For the last item, add extra scroll to ensure visibility
          if (isLastItem && menuRef.current) {
            // Add a bit more scroll for the last item
            setTimeout(() => {
              // Check if menuRef still exists before scrolling
              if (menuRef.current) {
                menuRef.current.scrollTop += 600; // Adjusted extra scroll value
              }
            }, 100); // Delay after scrollIntoView
          }
        }
      }, 50); // Small delay to ensure DOM has updated

      return () => clearTimeout(scrollTimer);
    }
    // Reset previousExpanded when nothing is expanded (e.g., initial state or after closing)
    else if (!expandedItem) {
      setPreviousExpanded(null);
    }
  }, [expandedItem, previousExpanded]); // Depend on both states

  // Modified to allow switching between expanded items
  const handleItemClick = (text) => {
    // If different item clicked or no item expanded, expand the clicked item
    if (!expandedItem || expandedItem !== text) {
      setExpandedItem(text);
    }
    // If the clicked item is already expanded, we'll leave it to the X button to collapse
  };

  // Function specifically for collapsing
  const handleCollapseClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setExpandedItem(null);
  };

  // New function to handle SVG element clicks
  const handleSvgElementClick = (e) => {
    if (!e?.target) return;

    // Try direct closest match first
    let clickedElement = e.target.closest('[data-action]');

    // If no direct match (Firefox may not support pointer-events: bounding-box),
    // emulate bounding-box behavior by testing click coordinates against
    // boundingClientRect of available action elements inside the menu.
    if (!clickedElement) {
      try {
        const rectPointX = e.clientX;
        const rectPointY = e.clientY;
        const actionEls = menuRef.current
          ? Array.from(menuRef.current.querySelectorAll('[data-action]'))
          : Array.from(document.querySelectorAll('.nav-menu [data-action]'));

        for (const el of actionEls) {
          const r = el.getBoundingClientRect();
          if (
            rectPointX >= r.left &&
            rectPointX <= r.right &&
            rectPointY >= r.top &&
            rectPointY <= r.bottom
          ) {
            clickedElement = el;
            break;
          }
        }
      } catch (err) {
        // fallback silently
      }
    }

    if (!clickedElement) return;

    const action = clickedElement.getAttribute('data-action');
    if (!action) return;

    switch (action) {
      case 'navigate': {
        const route = clickedElement.getAttribute('data-route');
        if (route) {
          // Prevent default navigation and handle via react-router
          e.preventDefault?.();
          e.stopPropagation?.();
          onNavigate?.();
          setIsOpen(false); // Close navbar when navigating
          navigate(route);
        }
        break;
      }
      case 'link': {
        const url = clickedElement.getAttribute('data-url');
        if (url) {
          e.preventDefault?.();
          e.stopPropagation?.();
          window.open(url, '_blank');
        }
        break;
      }
      case 'toggle': {
        const targetId = clickedElement.getAttribute('data-target');
        if (targetId) {
          e.preventDefault?.();
          e.stopPropagation?.();
          // Handle toggle logic here if needed
        }
        break;
      }
      default:
        break;
    }
  };

  const handleMenuClick = (e) => {
    handleSvgElementClick(e);
  };

  // Close menu when escape key is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setExpandedItem(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Emulate :hover for SVG anchors by tracking pointer/touch position
  useEffect(() => {
    if (!menuRef.current) return;

    const el = menuRef.current;

    let lastHovered = null;

    const findAndSetHover = (clientX, clientY) => {
      const actionEls = Array.from(el.querySelectorAll('[data-action]'));
      let found = null;
      for (const a of actionEls) {
        const r = a.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
          found = a;
          break;
        }
      }

      if (found !== lastHovered) {
        if (lastHovered) lastHovered.classList.remove('js-hover');
        if (found) found.classList.add('js-hover');
        lastHovered = found;
      }
    };

    const clearHover = () => {
      if (lastHovered) {
        lastHovered.classList.remove('js-hover');
        lastHovered = null;
      }
    };

    const onMove = (ev) => {
      if (!isOpen) return;
      const point = ev.touches && ev.touches[0] ? ev.touches[0] : ev;
      findAndSetHover(point.clientX, point.clientY);
    };

    const onLeave = () => {
      clearHover();
    };

    el.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('touchstart', onMove, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('touchend', onLeave);
    el.addEventListener('touchcancel', onLeave);

    return () => {
      clearHover();
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchstart', onMove);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('touchend', onLeave);
      el.removeEventListener('touchcancel', onLeave);
    };
  }, [isOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('nav-menu-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('nav-menu-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('nav-menu-open');
    };
  }, [isOpen]);

  return (
    <>
      {/* Hamburger Button - only visible when menu is closed */}
      {!isOpen && (
        <button
          className="nav-button"
          onClick={() => {
            setIsOpen(true);
            setExpandedItem(null); // Reset to default state when opening
          }}
          aria-label="Open navigation menu"
          aria-expanded={isOpen}
          data-cursor-invert="true"
          style={{
            position: isSticky ? 'relative' : 'fixed',
            top: isSticky ? 'auto' : '20px',
            left: isSticky ? 'auto' : '20px',
            zIndex: 20000
          }}
        >
          <div className="hamburger">
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </div>
        </button>
      )}

      {/* Fullscreen Menu */}
      <nav
        className={`nav-menu ${isOpen ? "open" : ""} ${expandedItem ? "has-expanded-item" : ""}`}
        ref={menuRef}
        onClick={handleMenuClick}
        aria-hidden={!isOpen}
        role="navigation"
        style={{ scrollBehavior: 'smooth' }}
        data-cursor-invert="true"
      >
        <div className="nav-top-buttons">
          <button
            className="home-btn"
            onClick={() => {
              onNavigate?.();
              setIsOpen(false);
              navigate('/');
            }}
            aria-label="Go to homepage"
            data-cursor-invert="true"
          >
            Home
          </button>

          <button
            className="close-btn"
            onClick={() => {
              setIsOpen(false);
              setExpandedItem(null);
            }}
            aria-label="Close navigation menu"
            data-cursor-invert="true"
          >
            ✕
          </button>
        </div>

        {/* Apply dynamic scaling to menu items container with 5% top padding */}
        <div
          className="menu-container"
          style={{
            transform: expandedItem ? 'none' : `scale(${menuScale})`,
            transformOrigin: 'center top',
            height: expandedItem ? 'auto' : '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: expandedItem ? 'flex-start' : 'center',
            transition: 'transform 0.3s ease',
            paddingTop: expandedItem ? '0' : '7%',
          }}
          data-cursor-invert="true"
        >
          <ul
            className={`nav-links ${expandedItem ? 'has-expanded' : ''}`}
            ref={navLinksRef}
            role="menu"
            data-cursor-invert="true"
          >
            {navItems.map((item, index) => {
              const isExpanded = expandedItem === item.text;
              const publicUrl = process.env.PUBLIC_URL || '';
              const iconPath = `${publicUrl}/${item.id}-icon.svg`;
              const { component: ExpandedComponent } = item;
              const isEven = index % 2 !== 0;

              // console.log("index",index)
              return (
                <li
                  key={item.id}
                  className={`nav-item ${isExpanded ? 'expanded' : ''}`}
                  style={index === 2 && isExpanded ? { marginBottom: '70px' } : {}}
                  role="menuitem"
                  data-cursor-invert="true"
                >
                  <div
                    // Only attach click handler if not expanded
                    onClick={!isExpanded ? () => handleItemClick(item.text) : undefined}
                    className="nav-item-header"
                    tabIndex={isOpen ? 0 : -1}
                    aria-expanded={isExpanded}
                    aria-controls={`${item.id}-content`}
                    data-cursor-invert="true"
                  >
                    <div className="nav-item-content" data-cursor-invert="true">
                      {/* Use img instead of object for better performance */}
                      <img
                        src={iconPath}
                        alt={`${item.text} icon`}
                        className="nav-icon"
                        loading="lazy"
                        data-cursor-invert="true"
                      />
                      <div className="nav-item-text" data-cursor-invert="true">{item.text}</div>

                      {/* Toggle button with appropriate semantics */}
                      <button
                        className="nav-arrow-button"
                        onClick={isExpanded ? handleCollapseClick : (e) => {
                          e.stopPropagation();
                          handleItemClick(item.text);
                        }}
                        aria-label={isExpanded ? `Collapse ${item.text}` : `Expand ${item.text}`}
                        data-cursor-invert="true"
                      >
                        {isExpanded ? (
                          <span className="nav-arrow" data-cursor-invert="true">✕</span>
                        ) : (
                          <DownArrowIcon
                            className="nav-arrow"
                            data-cursor-invert="true"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </div>

                    <div
                      className={`${isEven ? 'even-item' : 'underline-container'}`}
                      data-cursor-invert="true"
                      onClick={isExpanded ? handleSvgElementClick : undefined}
                    >
                      {isExpanded ? (
                        <>
                          <ExpandedComponent className="nav-underline expanded-underline" />
                        </>
                      ) : (
                        // Use regular img for non-expanded items
                        <img
                          src={`${publicUrl}/underline.svg`}
                          alt=""
                          className="nav-underline"
                          aria-hidden="true"
                          data-cursor-invert="true"
                        />
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Optional backdrop for better UX */}
      {isOpen && (
        <div
          className="nav-backdrop"
          onClick={() => {
            setIsOpen(false);
            setExpandedItem(null);
          }}
          aria-hidden="true"
          data-cursor-invert="true"
        />
      )}
    </>
  );
};

export default Navbar;